import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageShell from '@/components/Layout/PageShell';
import { Modal } from '@/components/UI/Modal';
import { PageHeader } from '@/components/UI/PageHeader';
import { Badge, type BadgeProps } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { Alert } from '@/components/UI/Alert';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { fieldClass } from '@/components/UI/fieldStyles';
import { getErrorDetail } from '@/utils/apiError';
import {
  useGetPlatformUserQuery,
  useUpdatePlatformUserMutation,
  useUpdatePlatformUserRolesMutation,
  useUpdatePlatformMembershipStatusMutation,
  useDeletePlatformUserMutation,
  PlatformUserDetail,
  PlatformUserMembership,
} from '@/redux/internal-services/platformAdminApi';

const STATUS_VARIANT: Record<string, BadgeProps['variant']> = {
  active:    'success',
  invited:   'accent',
  inactive:  'neutral',
  suspended: 'warning',
  deleted:   'danger',
};

const PLATFORM_ROLES = [
  { value: '', label: 'None (remove platform access)' },
  { value: 'platform_admin', label: 'Platform Admin' },
  { value: 'platform_support', label: 'Platform Support' },
];

const Avatar: React.FC<{ name?: string | null; url?: string | null; size?: 'sm' | 'lg' }> = ({ name, url, size = 'sm' }) => {
  const cls = size === 'lg' ? 'h-16 w-16 text-xl' : 'h-9 w-9 text-sm';
  if (url) return <img src={url} alt={name ?? ''} className={`${cls} rounded-full object-cover`} />;
  const initials = (name ?? '?').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div className={`${cls} rounded-full bg-accent-soft flex items-center justify-center text-accent-text font-semibold flex-shrink-0`}>
      {initials}
    </div>
  );
};

// ── Membership row ────────────────────────────────────────────────────────────

interface MembershipRowProps {
  m: PlatformUserMembership;
  userId: string;
  onUpdated: () => void;
}

const MembershipRow: React.FC<MembershipRowProps> = ({ m, userId, onUpdated }) => {
  const [updateStatus, { isLoading }] = useUpdatePlatformMembershipStatusMutation();
  const [msg, setMsg] = useState('');

  const toggle = async () => {
    const newStatus = m.status === 'active' ? 'Suspended' : 'Active';
    try {
      await updateStatus({ userId, tenantId: m.tenant_id, status: newStatus }).unwrap();
      setMsg('');
      onUpdated();
    } catch {
      setMsg('Failed to update.');
    }
  };

  const isPlatform = m.tenant_id === 'platform';

  return (
    <tr>
      <td className="td-cell">
        <div>
          <p className="text-sm font-medium text-ui-text">{m.tenant_name ?? m.tenant_id}</p>
          {isPlatform && <p className="text-xs text-ui-text3">Platform-level access</p>}
        </div>
      </td>
      <td className="td-cell">
        <div className="flex flex-wrap gap-1">
          {m.role_names.length > 0 ? m.role_names.map((r) => (
            <Badge key={r} variant="accent">{r}</Badge>
          )) : <span className="text-xs text-ui-text3">No roles</span>}
        </div>
      </td>
      <td className="td-cell">
        <Badge variant={STATUS_VARIANT[m.status] ?? 'neutral'} className="capitalize">
          {m.status}
        </Badge>
      </td>
      <td className="td-cell text-ui-text3">
        {m.joined_at ? new Date(m.joined_at).toLocaleDateString() : '—'}
      </td>
      <td className="td-cell text-right">
        {!isPlatform && m.status !== 'deleted' && (
          <Button
            variant="outline"
            size="xs"
            onClick={toggle}
            disabled={isLoading}
            className={m.status === 'active' ? 'text-warning-text' : 'text-success-text'}
          >
            {isLoading ? '...' : m.status === 'active' ? 'Suspend' : 'Activate'}
          </Button>
        )}
        {msg && <p className="text-xs text-danger-text mt-1">{msg}</p>}
      </td>
    </tr>
  );
};

// ── Edit Profile Section ──────────────────────────────────────────────────────

interface EditProfileProps {
  user: PlatformUserDetail;
  onUpdated: () => void;
}

const EditProfile: React.FC<EditProfileProps> = ({ user, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: user.name ?? '', email: user.email ?? '', phone: user.phone ?? '' });
  const [error, setError] = useState('');
  const [updateUser, { isLoading }] = useUpdatePlatformUserMutation();

  const platformMembership = user.memberships.find((m) => m.tenant_id === 'platform');
  const [editRoles, setEditRoles] = useState(false);
  const [platformRole, setPlatformRole] = useState(platformMembership?.tenant_role_ids[0] ?? '');
  const [updateRoles, { isLoading: savingRoles }] = useUpdatePlatformUserRolesMutation();

  const handleSaveProfile = async () => {
    setError('');
    try {
      await updateUser({ userId: user.user_id, data: { name: form.name, email: form.email, phone: form.phone || undefined } }).unwrap();
      setEditing(false);
      onUpdated();
    } catch (err: unknown) {
      setError(getErrorDetail(err) ?? 'Failed to save.');
    }
  };

  const handleSaveRoles = async () => {
    try {
      await updateRoles({ userId: user.user_id, data: { platform_role: platformRole || null } }).unwrap();
      setEditRoles(false);
      onUpdated();
    } catch {
      setError('Failed to update roles.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile info */}
      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-ui-text">Profile</h2>
          {!editing && (
            <Button variant="link" size="xs" className="text-accent-text" onClick={() => setEditing(true)}>Edit</Button>
          )}
        </div>

        {editing ? (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-ui-text2 mb-1">Full Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={fieldClass({ size: 'sm' })}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-ui-text2 mb-1">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={fieldClass({ size: 'sm' })}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-ui-text2 mb-1">Phone</label>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={fieldClass({ size: 'sm' })}
                placeholder="Optional"
              />
            </div>
            {error && <Alert tone="danger">{error}</Alert>}
            <div className="flex gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
              <Button size="sm" onClick={handleSaveProfile} disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        ) : (
          <dl className="space-y-3">
            {[
              { label: 'Name', value: user.name },
              { label: 'Email', value: user.email },
              { label: 'Phone', value: user.phone ?? '—' },
              { label: 'Email Verified', value: user.email_verified ? 'Yes' : 'No' },
              { label: 'Account Active', value: user.is_active ? 'Yes' : 'No' },
              { label: 'Member Since', value: user.created_at ? new Date(user.created_at).toLocaleDateString() : '—' },
            ].map(({ label, value }) => (
              <div key={label} className="flex gap-4">
                <dt className="text-xs font-medium text-ui-text3 w-32 flex-shrink-0">{label}</dt>
                <dd className="text-sm text-ui-text">{value ?? '—'}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      {/* Platform role */}
      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-ui-text">Platform Role</h2>
          {!editRoles && (
            <Button variant="link" size="xs" className="text-accent-text" onClick={() => setEditRoles(true)}>Edit</Button>
          )}
        </div>

        {editRoles ? (
          <div className="space-y-3">
            <select
              value={platformRole}
              onChange={(e) => setPlatformRole(e.target.value)}
              className={fieldClass({ size: 'sm' })}
            >
              {PLATFORM_ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            {error && <Alert tone="danger">{error}</Alert>}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditRoles(false)}>Cancel</Button>
              <Button size="sm" onClick={handleSaveRoles} disabled={savingRoles}>
                {savingRoles ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-ui-text">
            {platformMembership?.role_names[0] ?? <span className="text-ui-text3">No platform role</span>}
          </p>
        )}
      </div>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────

const UserDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [actionMsg, setActionMsg] = useState<{ type: 'error' | 'success'; text: string } | null>(null);

  const { data, isLoading, refetch } = useGetPlatformUserQuery(userId ?? '', { skip: !userId });
  const [deleteUser, { isLoading: deleting }] = useDeletePlatformUserMutation();

  const user: PlatformUserDetail | undefined = data?.data;

  const handleDelete = async () => {
    if (!userId) return;
    try {
      await deleteUser(userId).unwrap();
      navigate('/platform/users');
    } catch (err: unknown) {
      setActionMsg({ type: 'error', text: getErrorDetail(err) ?? 'Delete failed.' });
      setDeleteConfirm(false);
    }
  };

  if (isLoading) {
    return (
      <PageShell maxWidth="max-w-5xl" header={<div className="h-8" />}>
        <LoadingState label="Loading user..." />
      </PageShell>
    );
  }

  if (!user) {
    return (
      <PageShell maxWidth="max-w-5xl" header={<div className="h-8" />}>
        <EmptyState title={t('platform.userNotFound')} />
      </PageShell>
    );
  }

  const tenantMemberships = user.memberships.filter((m) => m.tenant_id !== 'platform');

  return (
    <PageShell
      maxWidth="max-w-5xl"
      header={
        <PageHeader
          back={
            <Button
              variant="link"
              size="xs"
              className="self-start text-ui-text2"
              onClick={() => navigate('/platform/users')}
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Users
            </Button>
          }
          title={
            <span className="flex items-center gap-4">
              <Avatar name={user.name} url={user.profile_picture_url} size="lg" />
              {user.name ?? '—'}
            </span>
          }
          subtitle={
            <>
              {user.email}
              {!user.is_active && (
                <Badge variant="danger" className="ml-2">Deleted</Badge>
              )}
            </>
          }
          actions={
            user.is_active ? (
              <Button variant="destructive-ghost" onClick={() => setDeleteConfirm(true)}>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete User
              </Button>
            ) : undefined
          }
        />
      }
    >

      {actionMsg && (
        <Alert tone={actionMsg.type === 'error' ? 'danger' : 'success'} className="mb-6">{actionMsg.text}</Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: profile + platform role */}
        <EditProfile user={user} onUpdated={refetch} />

        {/* Right: tenant memberships */}
        <div className="space-y-6">
          <div className="table-card">
            <div className="border-b border-ui-border px-6 py-4">
              <h2 className="text-base font-semibold text-ui-text">Tenant Memberships</h2>
              <p className="text-xs text-ui-text3 mt-0.5">{tenantMemberships.length} tenant{tenantMemberships.length !== 1 ? 's' : ''}</p>
            </div>
            {tenantMemberships.length === 0 ? (
              <EmptyState variant="inline" title="No tenant memberships." className="m-4" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="th-cell">Tenant</th>
                      <th className="th-cell">Roles</th>
                      <th className="th-cell">Status</th>
                      <th className="th-cell hidden sm:table-cell">Joined</th>
                      <th className="th-cell"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenantMemberships.map((m) => (
                      <MembershipRow key={m.tenant_id} m={m} userId={user.user_id} onUpdated={refetch} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete confirm */}
      {deleteConfirm && (
        <Modal
          title="Delete User"
          onClose={() => setDeleteConfirm(false)}
          onSave={handleDelete}
          saving={deleting}
          saveLabel="Delete"
          danger
          maxWidthClassName="max-w-sm"
        >
          <p className="text-sm text-ui-text2">
            Are you sure you want to delete <strong>{user.name ?? user.email}</strong>? This is a soft delete — all history is preserved but the user will lose access.
          </p>
        </Modal>
      )}
    </PageShell>
  );
};

export default UserDetailPage;
